import { MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import { GridList, useRefAutoUpdate, useStateWithRef, WithNativeHTMLProps } from "@kalyzee/kast-web-sdk/web-components";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { Meeting, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import React, { ForwardedRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import MeetingSessionMediaPlayer, { MeetingSessionMediaPlayerRef } from "./MeetingSessionMediaPlayer";

import styles from "./MeetingSessionPlayer.module.css";
import { PlayerContainerRenderer } from "./interfaces";
import MeetingPlayerSoundEffectOverlay from "./MeetingPlayerSoundEffectOverlay";
import { useVideoSubscriptions } from "../../hooks/ovenmedia";

export interface MeetingSessionPlayerProps extends WithNativeHTMLProps<HTMLDivElement> {
  meeting: Meeting;
  sessions: MeetingSession[];
  sessionId: string;
  ovenMediaSessions?: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>[];
  muted?: boolean;
  hideSoundEffect?: boolean;
  forceUseSession?: MeetingSession;
  autoOnOffVideo?: boolean;
  classNamePlayer?: string;
  playerStyle?: React.CSSProperties;
  videoStyle?: React.CSSProperties;
  className?: string;
  style?: React.CSSProperties;
  renderCustomContainer?: PlayerContainerRenderer;
}

export interface MeetingSessionPlayerRef {}

const MeetingSessionPlayer = React.forwardRef(
  (
    {
      meeting,
      sessions,
      sessionId,
      ovenMediaSessions,
      muted,
      hideSoundEffect,
      forceUseSession,
      autoOnOffVideo,
      classNamePlayer,
      playerStyle,
      videoStyle,
      className,
      style,
      nativeProps,
      renderCustomContainer,
    }: MeetingSessionPlayerProps,
    forwardRef: ForwardedRef<MeetingSessionPlayerRef>
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const meetingSessionMediaPlayerRefs = useRef<MeetingSessionMediaPlayerRef[]>([]);
    const [isPlayingSound, setIsPlayingSound, isPlayingSoundRef] = useStateWithRef(false);
    const session = useMemo(() => forceUseSession ?? sessions?.find((s) => s.id === sessionId), [sessions, sessionId, forceUseSession]);
    const videoSessions = useMemo(() => {
      const result: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>[] = [];
      if (session) {
        result.push(
          ...(ovenMediaSessions?.filter((s) => {
            if (s.data.session.id !== sessionId) return false;
            if (!session.medias.find((m) => m.id === s.data.mediaSession.id)) return false;
            return true;
          }) ?? [])
        );
      }

      return result;
    }, [session, ovenMediaSessions]);
    const autoOnOffVideoRef = useRefAutoUpdate(autoOnOffVideo);
    const subscribptionsAPI = useVideoSubscriptions();

    // ---------------------------------------------------------------------- //
    // ---------------------------  UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //
    
    const subscribeVideos = (
      sessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>[],
    ) => {
      if (!autoOnOffVideoRef.current) return;
      sessions.forEach((session) => {
        subscribptionsAPI.update([{ session, active: true }]);
      });
    };

    const unsubscribeVideos = (
      sessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>[],
    ) => {
      if (!autoOnOffVideoRef.current) return;
      sessions.forEach((session) => {
        subscribptionsAPI.update([{ session, active: false }]);
      });
    };
  
    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useImperativeHandle(forwardRef, () => ({}));

    useEffect(() => {
      subscribeVideos(videoSessions);
      setIsPlayingSound(false);
      return () => {
        unsubscribeVideos(videoSessions);
      }
    }, [videoSessions]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    const renderItem = (ovSession?: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>, className?: string) => {
      let lastRef: MeetingSessionMediaPlayerRef | null = null;
      const classes: string[] = [styles.item];
      if (className) classes.push(className);
      if (classNamePlayer) classes.push(classNamePlayer);
      return (
        <MeetingSessionMediaPlayer
          className={classes.join(" ")}
          style={playerStyle}
          videoStyle={videoStyle}
          ref={(ref) => {
            if (meetingSessionMediaPlayerRefs.current.find((r) => r === ref)) return;
            if (ref) {
              meetingSessionMediaPlayerRefs.current.push(ref);
            }
            if (lastRef !== ref) {
              meetingSessionMediaPlayerRefs.current = meetingSessionMediaPlayerRefs.current.filter((r) => r !== lastRef);
            }
            lastRef = ref;
          }}
          meeting={meeting}
          sessions={sessions}
          sessionId={sessionId}
          mediaSessionId={ovSession?.data.mediaSession?.id}
          ovenMediaSession={ovSession}
          muted={muted}
          hideSoundEffect
          forceUseSession={forceUseSession}
          callbacks={{
            playingSound: (playing) => {
              if (playing && !isPlayingSoundRef.current) {
                setIsPlayingSound(true);
              } else if (!playing && isPlayingSoundRef.current) {
                let onePlayerisPlayingSound = false;
                meetingSessionMediaPlayerRefs.current.forEach((r) => {
                  if (r.isPlayingSound()) onePlayerisPlayingSound = true;
                });
                if (!onePlayerisPlayingSound) setIsPlayingSound(false);
              }
            },
          }}
        />
      );
    };

    const renderContent = () => {
      if (sessions.length > 1) {
        return (
          <GridList<OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>>
            className={styles.content}
            gap={5}
            targetContentRatio={2.5}
            resizeObserverRef={containerRef}
            noPages
            hideDefaultPagination
            data={videoSessions}
            renderItem={(_, item) => {
              const defaultRender = () => <div />;
              if (!item) return defaultRender();
              return renderItem(item);
            }}
          />
        );
      } else if (sessions.length === 1) {
        return renderItem(videoSessions[0], styles.content);
      }
      return renderItem(undefined, styles.content);
    };

    const classes = [styles.container];
    if (className) classes.push(className);
    
    if (renderCustomContainer) {
      return (
        <>
          {renderCustomContainer(
            <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style} ref={containerRef}>
              {renderContent()}
              <MeetingPlayerSoundEffectOverlay enabled={!hideSoundEffect && isPlayingSound} />
            </div>,
            { isPlayingSound }
          )}
        </>
      );
    }

    return (
      <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style} ref={containerRef}>
        {renderContent()}
        <MeetingPlayerSoundEffectOverlay enabled={!hideSoundEffect && isPlayingSound} />
      </div>
    );
  }
);

export default MeetingSessionPlayer;
